import styled, { css } from 'styled-components'
import DynamicMarkdown from '../../app-components/dynamic-components/dynamic-markdown'
import { tabletBreakpoint, desktopBreakpoint, mobileBreakpoint, wideScreenBreakpoint } from 'react-components'
import ChevronRight from '../../../assets/icons/chevron-right.svg'

export const Container = styled.section(
    ({ theme }) => css`
        position: relative;
        overflow: visible;
        width: inherit;
        padding: 0.5rem 0;

        @media only screen and (min-width: ${desktopBreakpoint.min}px) {
            padding: 2rem 1.5rem 1rem;
        }

        @media only screen and (min-width: ${wideScreenBreakpoint.min}px) {
            padding: 2rem 4rem 1rem;
        }
    `,
)

export const ContentWrapper = styled.section(
    ({ theme }) => css`
        display: flex;
        flex-direction: column;
        margin: 1rem auto ${theme.spacing.medium};
        width: 100%;
        gap: ${theme.spacing.medium};
        align-items: flex-start;
        padding: 0 1rem;

        @media only screen and (min-width: ${tabletBreakpoint.min}px) {
            flex-direction: row;
            gap: 2rem;
            padding: 0 1.5rem;
        }

        @media only screen and (min-width: ${desktopBreakpoint.min}px) {
            padding: 0;
        }

        @media only screen and (min-width: ${wideScreenBreakpoint.min}px) {
            gap: ${theme.spacing.xxLarge};
        }
    `,
)

export const SidebarContainer = styled.div(
    ({ theme }) => css`
        position: static;
        top: auto;
        max-height: none;
        max-width: unset;
        width: 100%;
        border: 0.0625rem solid #d3d3d3;
        border-radius: 0.75rem;

        @media only screen and (min-width: ${tabletBreakpoint.min}px) {
            max-width: 17.5rem;
            align-self: flex-start;
            position: sticky;
            top: 6.875rem;
            max-height: calc(100vh - 6.875rem);
            overflow-y: auto;
            scroll-behavior: smooth;

            /* Hide scrollbar for Chrome, Safari, Edge */
            &::-webkit-scrollbar {
                display: none;
            }

            /* Hide scrollbar for Firefox */
            scrollbar-width: none;

            /* Hide scrollbar for IE/Edge */
            -ms-overflow-style: none;
        }
    `,
)

export const SectionLinksList = styled.ul`
    list-style: none;
    padding: 0;
    margin: 0;
`

interface HeadingItemProps {
    active?: boolean
}

export const SectionLinkContainer = styled.li<HeadingItemProps>(
    ({ theme, active }) => css`
        position: relative;
        cursor: pointer;
        display: flex;
        margin: 0.0625rem ${theme.spacing.xSmall} 0;
        padding: ${theme.spacing.small};
        background: ${active ? '#F5F5F5' : 'transparent'};
        border-radius: 0.75rem;
        transition: all 0.3s ease;

        &:first-child {
            margin: ${theme.spacing.xSmall} ${theme.spacing.xSmall} 0;
        }

        &:last-child {
            margin: 0.0625rem ${theme.spacing.xSmall} ${theme.spacing.xSmall};
        }

        &::after {
            content: '';
            position: absolute;
            left: 10%;
            right: 10%;
            bottom: 0;
            border-bottom: 0.0625rem dotted #f5f5f5;
            pointer-events: none;
        }

        &:last-child::after {
            display: none;
        }
    `,
)

export const SectionLink = styled.a(
    ({ theme }) => css`
        display: flex;
        align-items: center;
        text-decoration: none;
        color: inherit;
        width: 100%;
        gap: ${theme.spacing.xSmall};
        justify-content: space-between;
    `,
)

export const SectionLinkTitle = styled.p(
    ({ theme }) => css`
        flex: 1;
        font-family: ${theme.typography.font.family.lato};
        font-size: ${theme.typography.font.size.medium};
        font-weight: 400;
        line-height: 1.5rem;
        color: ${theme.colors.tfwBlack99};
    `,
)

export const ArrowContainer = styled.div<{ active?: boolean }>`
    display: flex;
    align-items: center;
    justify-content: center;
    height: 1.5rem;
    width: 1.5rem;
    border-radius: 50%;
    background: ${({ active }) => (active ? '#fff' : '#f5f5f5')};
    transition: background 0.3s ease;
`

export const ArrowRightIcon = styled(ChevronRight)`
    height: 100%;
    width: 100%;

    & path {
        stroke: #a5a5a5;
    }

    transform: rotate(90deg);
    transform-origin: center; /* ensures rotation around center */

    @media only screen and (min-width: ${mobileBreakpoint.max}px) {
        transform: none;
    }
`

export const ContentContainer = styled.div`
    flex: 1;
    min-width: 0;
`

export const ContentSection = styled.div(
    ({ theme }) => css`
        margin-bottom: ${theme.spacing.xxLarge};
        scroll-margin-top: 6.875rem;

        /* creates a new block formatting context so the float clears */
        display: flow-root;

        &:last-child {
            margin-bottom: 0;
        }
    `,
)

export const HeadingImageWrapper = styled.div`
    display: flex;
    flex-direction: column;

    @media only screen and (min-width: ${tabletBreakpoint.min}px) {
        flex-direction: column-reverse;
    }

    @media only screen and (min-width: ${desktopBreakpoint.min}px) {
        display: block;
    }
`

export const SectionHeading = styled.h3(
    ({ theme }) => css`
        font-family: ${theme.typography.font.family.lato};
        font-weight: 600;
        font-size: ${theme.typography.font.size.large};
        line-height: 1.75rem;
        margin-bottom: ${theme.spacing.xSmall};
        color: ${theme.colors.tfwBlack99};

        @media only screen and (min-width: ${tabletBreakpoint.max}px) {
            margin-bottom: ${theme.spacing.small};
        }

        @media only screen and (min-width: ${desktopBreakpoint.max}px) {
            margin-bottom: ${theme.spacing.xxSmall};
        }
    `,
)

export const ImageWrapper = styled.div(
    ({ theme }) => css`
        margin: 0 auto ${theme.spacing.xSmall};
        width: 100%;
        max-width: none;
        border-radius: 1rem;
        background-color: #f0f0f0;
        overflow: hidden;

        @media only screen and (min-width: ${tabletBreakpoint.min}px) {
            margin: ${theme.spacing.large} auto;
            max-width: 18.75rem;
        }

        @media only screen and (min-width: ${desktopBreakpoint.min}px) {
            float: right;
            width: 20.5rem;
            aspect-ratio: 20.5 / 13.625;
            margin: ${theme.spacing.xxLarge} 0 ${theme.spacing.large} ${theme.spacing.large};
        }
    `,
)

export const SectionImage = styled.img`
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
`

export const CustomMarkdown = styled(DynamicMarkdown)(
    ({ theme }) => css`
        & ul,
        li {
            font: ${theme.typography.font.lato.weight.regular} ${theme.typography.font.size.medium} / 1.5rem
                ${theme.typography.font.family.lato};
            color: ${theme.colors.tfwBlack99};
            margin: ${theme.spacing.small} ${theme.spacing.medium};
            padding: 0;
        }

        & p,
        h1,
        h2,
        h3,
        h4,
        h5,
        h6 {
            font: ${theme.typography.font.lato.weight.regular} ${theme.typography.font.size.medium} / 1.5rem
                ${theme.typography.font.family.lato};
            color: ${theme.colors.tfwBlack99};
            margin: ${theme.spacing.medium} 0;
            padding: 0;
        }

        & strong {
            color: #4a4a4a;
        }

        & p:first-child {
            margin-top: 0;
        }

        & p:last-child {
            margin-bottom: 0;
        }

        & a {
            color: #016bd4;
            text-decoration: none;

            &:hover {
                color: #0159b8;
                text-decoration: underline;
            }
        }
    `,
)
